import { useI18n } from '../../i18n'
import type { WebsitePage } from '../../types'
import { Plus, Trash2, ChevronUp, ChevronDown, FileText, Eye, EyeOff } from 'lucide-react'

interface Props {
  pages: WebsitePage[]
  activePage: WebsitePage | null
  onPageSelect: (page: WebsitePage) => void
  onAddPage: (data: { slug: string; title: string; titleEn?: string }) => Promise<string | null>
  onDeletePage: (pageId: string) => Promise<boolean>
  onReorderPages: (pageOrders: Array<{ pageId: string; order: number }>) => Promise<boolean>
}

export default function WebsitePageList({ pages, activePage, onPageSelect, onAddPage, onDeletePage, onReorderPages }: Props) {
  const { t } = useI18n()

  const sorted = [...(pages || [])].sort((a, b) => a.pageOrder - b.pageOrder)

  const handleAdd = async () => {
    const slug = `page-${Date.now()}`
    await onAddPage({ slug, title: t('新页面', 'New Page'), titleEn: 'New Page' })
  }

  const handleMoveUp = async (index: number) => {
    if (index <= 0) return
    const newOrders = sorted.map((p, i) => {
      if (i === index) return { pageId: p.id, order: i - 1 }
      if (i === index - 1) return { pageId: p.id, order: i }
      return { pageId: p.id, order: i }
    })
    await onReorderPages(newOrders)
  }

  const handleMoveDown = async (index: number) => {
    if (index >= sorted.length - 1) return
    const newOrders = sorted.map((p, i) => {
      if (i === index) return { pageId: p.id, order: i + 1 }
      if (i === index + 1) return { pageId: p.id, order: i }
      return { pageId: p.id, order: i }
    })
    await onReorderPages(newOrders)
  }

  return (
    <div className="p-3">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-medium text-gray-500">{t('页面列表', 'Pages')}</span>
        <button onClick={handleAdd}
          className="flex items-center gap-1 text-[11px] text-orange-500 hover:text-orange-600 font-medium">
          <Plus className="h-3 w-3" />
          {t('添加', 'Add')}
        </button>
      </div>

      <div className="space-y-1">
        {sorted.map((page, i) => (
          <div key={page.id}
            className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm transition-all cursor-pointer group ${
              activePage?.id === page.id
                ? 'bg-orange-50 text-[#1a1a1a] font-medium'
                : 'text-gray-500 hover:bg-black/[0.03] hover:text-[#1a1a1a]'
            }`}
            onClick={() => onPageSelect(page)}
          >
            <FileText className="h-3.5 w-3.5 shrink-0 opacity-40" />
            <span className="flex-1 truncate">{page.title}</span>
            <div className="hidden group-hover:flex items-center gap-0.5">
              <button onClick={(e) => { e.stopPropagation(); handleMoveUp(i) }}
                className="p-0.5 rounded text-gray-400 hover:text-[#1a1a1a]">
                <ChevronUp className="h-3 w-3" />
              </button>
              <button onClick={(e) => { e.stopPropagation(); handleMoveDown(i) }}
                className="p-0.5 rounded text-gray-400 hover:text-[#1a1a1a]">
                <ChevronDown className="h-3 w-3" />
              </button>
              {sorted.length > 1 && (
                <button onClick={(e) => { e.stopPropagation(); onDeletePage(page.id) }}
                  className="p-0.5 rounded text-gray-400 hover:text-red-500">
                  <Trash2 className="h-3 w-3" />
                </button>
              )}
            </div>
            {!page.visible && <EyeOff className="h-3 w-3 text-gray-300 shrink-0" />}
          </div>
        ))}
      </div>
    </div>
  )
}
